"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { EditListingDialog } from "@/components/my-listings/edit-listing-dialog"
import { editListingEvents } from "@/lib/edit-listing-events"
import {
  EMPTY_EDIT_FORM_DATA,
  buildEditFormState,
  buildEditListingRow,
  snapshotEditForm,
  type EditListingFormData,
} from "@/lib/edit-listing-state"
import type { ShippingOption } from "@/components/my-listings/edit-listing-dialog"

export function GlobalEditListingDialog() {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [listing, setListing] = useState<any>(null)
  const [formData, setFormData] = useState<EditListingFormData>(EMPTY_EDIT_FORM_DATA)
  const [selectedShippingMethods, setSelectedShippingMethods] = useState<string[]>([])
  const [customShippingOptions, setCustomShippingOptions] = useState<ShippingOption[]>([])
  const initialSnapshotRef = useRef<string>("")
  const initialListingRef = useRef<any>(null)
  const pendingListingIdRef = useRef<string | null>(null)

  useEffect(() => {
    let isMounted = true
    const unsub = editListingEvents.subscribe(({ listingId, listing: initialListing }) => {
      if (!isMounted) return
      initialListingRef.current = initialListing
      pendingListingIdRef.current = listingId
      setOpen(true)
      setLoading(true)
      setListing(null)

      const applyState = (data: any) => {
        if (!isMounted || pendingListingIdRef.current !== listingId) return
        const row = buildEditListingRow(data, listingId, initialListingRef.current ?? undefined)
        const next = buildEditFormState(row)
        setListing(row)
        setFormData(next.formData)
        setSelectedShippingMethods(next.selectedShippingMethods)
        setCustomShippingOptions(next.customShippingOptions)
        initialSnapshotRef.current = snapshotEditForm(next.formData)
        setLoading(false)
      }

      fetch(`/api/listings/${listingId}/edit`, { credentials: "include" })
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Fetch failed"))))
        .then((data: any) => applyState(data))
        .catch(() => applyState(null))
    })
    return () => {
      isMounted = false
      unsub()
    }
  }, [])

  const handleFormChange = useCallback((key: string, value: any) => {
    if (key === "selectedShippingMethods") {
      setSelectedShippingMethods(Array.isArray(value) ? value : [])
      return
    }
    if (key === "customShippingOptions") {
      setCustomShippingOptions(Array.isArray(value) ? value : [])
      return
    }
    setFormData((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next)
    if (!next) {
      setListing(null)
      setLoading(false)
    }
  }, [])

  const handleSave = useCallback((updatedListing?: any) => {
    if (updatedListing) {
      editListingEvents.emitSaved({ listing: updatedListing })
    }
    setOpen(false)
    setListing(null)
  }, [])

  if (!open) return null

  const hasFormChanges =
    !loading && listing ? snapshotEditForm(formData) !== initialSnapshotRef.current : false

  return (
    <EditListingDialog
      open={open}
      onOpenChangeAction={handleOpenChange}
      listing={listing}
      onSaveAction={handleSave}
      hideQuantity={(listing as any)?.visibility === "chat_private"}
      formData={loading ? EMPTY_EDIT_FORM_DATA : formData}
      onFormChangeAction={handleFormChange}
      selectedShippingMethods={loading ? [] : selectedShippingMethods}
      customShippingOptions={loading ? [] : customShippingOptions}
      elevatedForChat
      disableSaveWhenUnchanged
      hasFormChanges={hasFormChanges}
      loading={loading}
    />
  )
}
